import type { TableName } from './rows';

/**
 * Das lokale Schema — eine Teilmenge des Relay-Schemas.
 *
 * Übertragung der Typen: `uuid`, `string(n)`, `text`, `char(n)` und `json` werden `TEXT`,
 * `int`/`long` werden `INTEGER`, `bool` bleibt `'Y'`/`'N'` mit `CHECK`. Wertelisten sind
 * `TEXT` mit `CHECK`, die Werte wörtlich aus dem Relay-Dokument.
 *
 * Die Reihenfolge ist bindend: ein Fremdschlüssel darf nur auf eine bereits angelegte Tabelle
 * zeigen. `schema.test.ts` prüft das bei jedem Lauf.
 */

export const SCHEMA_VERSION = 11;

export const TABLES: readonly TableName[] = [
  'Exam', 'ExamPart', 'ExamItemContainer', 'ExamItem', 'ExamQuestion', 'ExamAnswer',
  'Examiner', 'ExamExaminer', 'Actor', 'ExamActor',
  'Candidate', 'ExamCandidate', 'CandidateExamMetadata',
  'Media',
  'PredefinedCommentPhraseList', 'PredefinedCommentPhrase',
  'ExamItemPredefinedCommentPhraseList',
  'ExamItemContainerPredefinedCommentPhraseList',
  'ExamLogType', 'ExamLog',
  'Client', 'ClientSettingValue',
  'ClassicKennung',
  'Dateiuebertragung',
];

export const DDL: readonly string[] = [
  'PRAGMA foreign_keys = ON',

  `CREATE TABLE Exam (
     id TEXT PRIMARY KEY,
     name TEXT NOT NULL,
     description TEXT,
     examType TEXT NOT NULL DEFAULT 'SIMPLE'
       CHECK (examType IN ('OSCE','SIMPLE','PROGRESS','BUNDLE')),
     durationSeconds INTEGER DEFAULT 0,
     startDate TEXT NOT NULL,
     startTime TEXT NOT NULL DEFAULT '1000',
     endDate TEXT,
     endTime TEXT NOT NULL DEFAULT '1200',
     lastModifiedDate TEXT NOT NULL DEFAULT '19000101000000000',
     notice TEXT DEFAULT ''
   )`,

  `CREATE TABLE ExamPart (
     id TEXT PRIMARY KEY,
     examID TEXT NOT NULL REFERENCES Exam(id) ON DELETE CASCADE,
     name TEXT NOT NULL,
     orderNo INTEGER NOT NULL DEFAULT 0
   )`,

  `CREATE TABLE ExamItemContainer (
     id TEXT PRIMARY KEY,
     examPartID TEXT NOT NULL REFERENCES ExamPart(id) ON DELETE CASCADE,
     name TEXT NOT NULL,
     type TEXT NOT NULL
       CHECK (type IN ('STATION','KEYFEATURE','STARSHAPED','EXTENDED_MATCHING')),
     durationSeconds INTEGER,
     orderNo INTEGER NOT NULL DEFAULT 0,
     vignette TEXT,
     comment TEXT
   )`,

  `CREATE TABLE ExamItem (
     id TEXT PRIMARY KEY,
     examItemContainerID TEXT NOT NULL REFERENCES ExamItemContainer(id) ON DELETE CASCADE,
     name TEXT NOT NULL,
     type TEXT NOT NULL
       CHECK (type IN ('TASK','FREETEXT','SINGLE_CHOICE','MULTIPLE_CHOICE')),
     orderNo INTEGER NOT NULL DEFAULT 0,
     maxPointsNumerator INTEGER NOT NULL DEFAULT 1,
     maxPointsDenominator INTEGER NOT NULL DEFAULT 1,
     anchorCriterion TEXT,
     comment TEXT,
     CHECK (maxPointsDenominator > 0)
   )`,

  `CREATE TABLE ExamQuestion (
     id TEXT PRIMARY KEY,
     examItemID TEXT NOT NULL REFERENCES ExamItem(id) ON DELETE CASCADE,
     questionText TEXT,
     vignette TEXT
   )`,

  `CREATE TABLE ExamAnswer (
     id TEXT PRIMARY KEY,
     examQuestionID TEXT NOT NULL REFERENCES ExamQuestion(id) ON DELETE CASCADE,
     answerText TEXT,
     orderNo INTEGER NOT NULL DEFAULT 0,
     scalaValues TEXT,
     maxPointsNumerator INTEGER NOT NULL DEFAULT 1,
     maxPointsDenominator INTEGER NOT NULL DEFAULT 1,
     comment TEXT,
     CHECK (maxPointsDenominator > 0)
   )`,

  `CREATE TABLE Examiner (
     id TEXT PRIMARY KEY,
     firstName TEXT NOT NULL,
     lastName TEXT NOT NULL,
     groupName TEXT NOT NULL DEFAULT '',
     key TEXT,
     password TEXT NOT NULL
   )`,

  `CREATE TABLE ExamExaminer (
     id TEXT PRIMARY KEY,
     examID TEXT NOT NULL REFERENCES Exam(id) ON DELETE CASCADE,
     examinerID TEXT NOT NULL REFERENCES Examiner(id) ON DELETE CASCADE,
     UNIQUE (examID, examinerID)
   )`,

  `CREATE TABLE Actor (
     id TEXT PRIMARY KEY,
     firstName TEXT NOT NULL,
     lastName TEXT NOT NULL,
     groupName TEXT
   )`,

  `CREATE TABLE ExamActor (
     id TEXT PRIMARY KEY,
     examID TEXT NOT NULL REFERENCES Exam(id) ON DELETE CASCADE,
     actorID TEXT NOT NULL REFERENCES Actor(id) ON DELETE CASCADE,
     UNIQUE (examID, actorID)
   )`,

  `CREATE TABLE Candidate (
     id TEXT PRIMARY KEY,
     firstName TEXT NOT NULL,
     lastName TEXT NOT NULL,
     matriculationNumber TEXT
   )`,

  `CREATE TABLE ExamCandidate (
     id TEXT PRIMARY KEY,
     examID TEXT NOT NULL REFERENCES Exam(id) ON DELETE CASCADE,
     candidateID TEXT NOT NULL REFERENCES Candidate(id) ON DELETE CASCADE,
     examCandidateNo TEXT,
     UNIQUE (examID, candidateID)
   )`,

  `CREATE TABLE CandidateExamMetadata (
     id TEXT PRIMARY KEY,
     candidateID TEXT NOT NULL REFERENCES Candidate(id) ON DELETE CASCADE,
     examID TEXT NOT NULL REFERENCES Exam(id) ON DELETE CASCADE,
     status TEXT NOT NULL DEFAULT 'NOT_STARTED'
       CHECK (status IN ('NOT_STARTED','RUNNING','PAUSED','SUBMITTED','ABORTED')),
     remainingSeconds INTEGER,
     completedItems INTEGER,
     totalItems INTEGER,
     lastStartDate TEXT,
     lastModifiedDate TEXT,
     UNIQUE (examID, candidateID)
   )`,

  `CREATE TABLE Media (
     id TEXT PRIMARY KEY,
     name TEXT NOT NULL,
     mimeType TEXT,
     referenceType TEXT NOT NULL
       CHECK (referenceType IN ('EXAM','EXAM_PART','EXAM_ITEM_CONTAINER','EXAM_ITEM',
                                'EXAM_QUESTION','EXAM_ANSWER','CANDIDATE','EXAMINER',
                                'ACTOR','INSTITUTION')),
     referenceIdentifier TEXT NOT NULL,
     referenceSubType TEXT,
     size INTEGER NOT NULL DEFAULT -1,
     orderNo INTEGER NOT NULL DEFAULT 0,
     description TEXT
   )`,

  `CREATE TABLE PredefinedCommentPhraseList (
     id TEXT PRIMARY KEY,
     name TEXT NOT NULL
   )`,

  `CREATE TABLE PredefinedCommentPhrase (
     id TEXT PRIMARY KEY,
     predefinedCommentPhraseListID TEXT NOT NULL
       REFERENCES PredefinedCommentPhraseList(id) ON DELETE CASCADE,
     parentPredefinedCommentPhraseID TEXT
       REFERENCES PredefinedCommentPhrase(id) ON DELETE CASCADE,
     name TEXT NOT NULL,
     fullText TEXT NOT NULL,
     orderNo INTEGER NOT NULL DEFAULT 0
   )`,

  `CREATE TABLE ExamItemPredefinedCommentPhraseList (
     id TEXT PRIMARY KEY,
     examItemID TEXT NOT NULL REFERENCES ExamItem(id) ON DELETE CASCADE,
     predefinedCommentPhraseListID TEXT NOT NULL
       REFERENCES PredefinedCommentPhraseList(id) ON DELETE CASCADE,
     UNIQUE (examItemID, predefinedCommentPhraseListID)
   )`,

  `CREATE TABLE ExamLogType (
     id TEXT PRIMARY KEY,
     name TEXT NOT NULL UNIQUE,
     description TEXT NOT NULL,
     referenceType TEXT
       CHECK (referenceType IS NULL OR referenceType IN
              ('EXAM','EXAM_PART','EXAM_ITEM_CONTAINER','EXAM_ITEM','EXAM_QUESTION','EXAM_ANSWER')),
     valueFormat TEXT NOT NULL DEFAULT 'NONE'
       CHECK (valueFormat IN ('NONE','TEXT','NUMBER','JSON','JSON_ARRAY','MEDIA_ID'))
   )`,

  `CREATE TABLE ExamLog (
     id TEXT PRIMARY KEY,
     actorID TEXT REFERENCES Actor(id) ON DELETE SET NULL,
     candidateID TEXT REFERENCES Candidate(id) ON DELETE SET NULL,
     examID TEXT REFERENCES Exam(id) ON DELETE RESTRICT,
     examinerID TEXT REFERENCES Examiner(id) ON DELETE SET NULL,
     examLogTypeID TEXT NOT NULL REFERENCES ExamLogType(id) ON DELETE RESTRICT,
     creationDate TEXT NOT NULL,
     deviceIdentifier TEXT NOT NULL,
     deviceName TEXT NOT NULL,
     pointsNumerator INTEGER,
     pointsDenominator INTEGER,
     referenceIdentifier TEXT,
     referenceType TEXT
       CHECK (referenceType IS NULL OR referenceType IN
              ('EXAM','EXAM_PART','EXAM_ITEM_CONTAINER','EXAM_ITEM','EXAM_QUESTION','EXAM_ANSWER')),
     storeDate TEXT,
     value TEXT NOT NULL DEFAULT ''
   )`,

  'CREATE INDEX ExamLog_stand ON ExamLog (examID, candidateID, examLogTypeID, referenceIdentifier, creationDate)',
  'CREATE INDEX ExamLog_offen ON ExamLog (storeDate, creationDate)',

  `CREATE TABLE Client (
     id TEXT PRIMARY KEY,
     deviceIdentifier TEXT NOT NULL UNIQUE,
     deviceName TEXT,
     clientType TEXT NOT NULL DEFAULT 'BROWSER'
       CHECK (clientType IN ('UNKNOWN','TABLET','BROWSER','DESKTOP')),
     appVersion TEXT,
     osVersion TEXT,
     currentExamID TEXT REFERENCES Exam(id) ON DELETE SET NULL,
     currentCandidateID TEXT REFERENCES Candidate(id) ON DELETE SET NULL,
     currentExaminerID TEXT REFERENCES Examiner(id) ON DELETE SET NULL,
     currentActorID TEXT REFERENCES Actor(id) ON DELETE SET NULL,
     currentStatus TEXT NOT NULL DEFAULT 'INACTIVE'
       CHECK (currentStatus IN ('INACTIVE','LOGGED_IN','PAUSED')),
     online TEXT NOT NULL DEFAULT 'N' CHECK (online IN ('Y','N')),
     batteryLevelPercent INTEGER,
     voltageStatus TEXT CHECK (voltageStatus IS NULL OR voltageStatus IN ('PLUGGED_IN','BATTERY_MODE')),
     freeSpaceKb INTEGER
   )`,

  `CREATE TABLE ClientSettingValue (
     id TEXT PRIMARY KEY,
     clientID TEXT NOT NULL REFERENCES Client(id) ON DELETE CASCADE,
     clientSettingID TEXT NOT NULL,
     clientSettingName TEXT NOT NULL,
     value TEXT NOT NULL,
     UNIQUE (clientID, clientSettingName)
   )`,

];

/**
 * Der Aufstieg von Fassung 1 auf 2.
 *
 * ⚠️ **`DDL` oben ist Fassung 1 und bleibt es — auch wenn das Schema weiterwächst.** Das
 * SQLite-Plugin führt die Schritte **sequenziell über alle Fassungen** zwischen der
 * vorgefundenen und der verlangten aus, und „Fassung 0 ist eine leere Ablage"
 * (`docs/UpgradeDatabaseVersion.md`). Eine frische Ablage durchläuft also `DDL` **und** diesen
 * Aufstieg. Stünde `transferred` schon in `DDL`, träfe das `ALTER TABLE` unten auf eine Spalte,
 * die es bereits gibt — und der Aufstieg schlüge fehl, und zwar **nur auf neuen Geräten**, wo
 * es beim Entwickeln am wenigsten auffällt.
 *
 * Beide Wege enden deshalb bei genau demselben Schema. Wer hier etwas ergänzt, ergänzt es nicht
 * oben.
 *
 * Änderungen dieser Fassung:
 *
 * 1. **Die Übertragungsmarke bekommt eine eigene Spalte.** Bisher stand sie in `storeDate`
 *    (leer = offen), und das war falsch: `storeDate` ist laut `METIS-schema.md:1127` die
 *    **Ankunftszeit** und `NOT NULL`.
 * 2. **`ClassicKennung`** für die Rohkennungen, die unsere eigenen nicht mittragen — bisher genau
 *    eine: die Antwortzeile einer global bewerteten Aufgabe (Spec §4.3). Keine Relay-Tabelle;
 *    sie beschreibt die Herkunft heruntergeladener Daten und gibt es nur im Classic-Modus. Die
 *    Zeilen hängen an ihrer Prüfung — ohne `examID` blieben sie beim Löschen als Waisen stehen
 *    und zeigten auf Aufgaben, die es nicht mehr gibt.
 *
 * ⚠️ Die Reihenfolge der Anweisungen ist bindend: erst füllen, dann umdeuten. `storeDate` bleibt
 * formal nullbar — SQLite kann eine Spalte nicht nachträglich auf `NOT NULL` setzen, und ein
 * Neubau der Tabelle wäre auf einem Gerät mit echten Prüfungsdaten das grössere Risiko. Die
 * Anwendung schreibt die Spalte immer (`examLog.ts::appendLog`).
 */
export const AUFSTIEG_2: readonly string[] = [
  "ALTER TABLE ExamLog ADD COLUMN transferred TEXT NOT NULL DEFAULT 'N'",
  // Was bisher einen Stempel trug, war übertragen — das war die alte Bedeutung von `storeDate`.
  "UPDATE ExamLog SET transferred = 'Y' WHERE storeDate IS NOT NULL",
  // Und was keinen trug, bekommt eine Speicherzeit. `creationDate` ist die beste verfügbare
  // Schätzung: lokal fallen Bewerten und Speichern ohnehin zusammen.
  'UPDATE ExamLog SET storeDate = creationDate WHERE storeDate IS NULL',
  'DROP INDEX IF EXISTS ExamLog_offen',
  'CREATE INDEX ExamLog_offen ON ExamLog (transferred, creationDate)',
  `CREATE TABLE IF NOT EXISTS ClassicKennung (
     id TEXT PRIMARY KEY,
     examID TEXT NOT NULL REFERENCES Exam(id) ON DELETE CASCADE,
     roh TEXT NOT NULL
   )`,
];

/**
 * Fassung 3: der Übertragungsstand der Dateien (Relay-Gleis, Etappe 5).
 *
 * ⚠️ **Nicht in `DDL`.** Eine frische Ablage durchläuft `DDL` **und** jeden Aufstieg; stünde
 * die Tabelle oben, träfe das `CREATE TABLE` hier auf eine, die es schon gibt.
 *
 * Die Tabelle steht **nicht** im Relay-Schema, und das mit Absicht: ob eine Datei schon
 * draussen ist, ist Gerätewissen und entstünde auf dem Server aus nichts. Begründet in
 * `docs/spec-relay-etappe5-dateien.md` §5.1; das Vorbild ist `ClassicKennung` in `AUFSTIEG_2`.
 *
 * **Die `id` ist der Pfad** unterhalb von Documents, kein zweiter Schlüssel daneben: der Pfad
 * ist in der Ablage ohnehin eindeutig, und `port.ts` wie der Browser-Adapter (`web.ts`,
 * `keyPath: 'id'`) bestehen auf diesem Namen.
 *
 * **Kein Fremdschlüssel auf `Exam`.** Eine Prüfung kann vom Gerät verschwinden, während die
 * Dateien noch liegen; die Zeile soll das überleben. `examID` bleibt eine blosse Notiz.
 */
export const AUFSTIEG_3: readonly string[] = [
  `CREATE TABLE IF NOT EXISTS Dateiuebertragung (
     id TEXT PRIMARY KEY,
     examID TEXT,
     art TEXT NOT NULL,
     erledigt TEXT NOT NULL DEFAULT 'N' CHECK (erledigt IN ('Y','N')),
     storeDate TEXT,
     versuche INTEGER NOT NULL DEFAULT 0,
     naechsterVersuch TEXT,
     letzterFehler TEXT
   )`,
  'CREATE INDEX IF NOT EXISTS Dateiuebertragung_offen ON Dateiuebertragung (erledigt)',
];

/**
 * Fassung 4: die Obergrenze für abgewiesene Dateien.
 *
 * ⚠️ **Gezählt werden nur Ablehnungen (4xx), nicht Fehlschläge überhaupt.** Der Rückzug
 * deckelt bei 60 Sekunden; zählte man jeden Fehlversuch, wäre eine Datei nach viereinhalb
 * Minuten ohne Netz für immer aufgegeben — genau in der Lage, in der Wiederholen richtig ist.
 * Ein 4xx dagegen ist eine überlegte Antwort des Servers: fünfmal dieselbe zu hören genügt.
 *
 * Warum eine eigene Spalte und kein dritter Wert in `erledigt`: `erledigt` ist ein `bool` und
 * trägt `'Y'`/`'N'`, wie jede bool-Spalte dieses Schemas (`schema.test.ts` prüft das). Der
 * Zustand „aufgegeben" ist ausserdem **ableitbar** (`erledigt='N'` und `ablehnungen >=`
 * Grenze) — und was ableitbar ist, wird nicht zusätzlich gespeichert, sonst laufen zwei
 * Wahrheiten auseinander.
 */
export const AUFSTIEG_4: readonly string[] = [
  'ALTER TABLE Dateiuebertragung ADD COLUMN ablehnungen INTEGER NOT NULL DEFAULT 0',
];

/**
 * Fassung 5: die Simulationsprüfung.
 *
 * **Auftrag des Nutzers vom 2026-09-18:** eine Prüfung mit `simulative = true` ist eine
 * Schulungs- oder Vorführprüfung — nichts davon soll aufgezeichnet werden.
 *
 * ⛔ **Die Regel sitzt vollständig bei der App.** Der Server filtert **nicht**: schickt das
 * Gerät für eine solche Prüfung eine Zeile, wird sie gespeichert wie bei jeder echten. Deshalb
 * sendet die App gar nichts (`server/sync.ts`, `server/anhaenge.ts`).
 *
 * ⚠️ **Deshalb muss es in der Ablage stehen und nicht nur im Katalog des Servers.** Das Gerät
 * arbeitet nach dem Herunterladen offline; stünde das Feld nur in `exam/available`, wüsste die
 * App mitten in der Prüfung nicht mehr, dass nichts aufgezeichnet wird — und der Prüfer
 * bewertete ins Leere, ohne es zu merken.
 *
 * ⚠️ **Classic kennt das Feld nicht.** Eine über das Classic-Gleis geholte Prüfung ist immer
 * echt; die Vorgabe `'N'` ist dort die Wahrheit, keine Lücke.
 */
export const AUFSTIEG_5: readonly string[] = [
  "ALTER TABLE Exam ADD COLUMN simulative TEXT NOT NULL DEFAULT 'N' CHECK (simulative IN ('Y','N'))",
];

/**
 * Fassung 6: die Bausteine der **Station**.
 *
 * ⭐ **Berichtigung durch die Relay-Seite am 2026-09-20.** Die Station führt eine eigene
 * Bausteinliste (`ExamItemContainer.textBlockContainerID`) — der Auftrag des Nutzers vom
 * 2026-09-18 lautete „zuordenbar zu Station **und** Aufgabe". Unser Papier ging davon aus,
 * beim Stationskommentar gebe es keine Bausteine, weil dort keine Aufgabe steht; eine
 * gepflegte Liste bliebe damit unsichtbar.
 *
 * ⚠️ **Eine eigene Tabelle statt einer zweiten Spalte.** In
 * `ExamItemPredefinedCommentPhraseList` ist `examItemID` `NOT NULL` und zeigt auf `ExamItem`.
 * Eine Station dort einzutragen hiesse, den Fremdschlüssel aufzuweichen.
 *
 * ⚠️ **Classic legt hier nichts an.** Der Classic-Server kennt die Zuordnung nur je Aufgabe
 * (`osce.phrases`, an alle Aufgaben gehängt) — die leere Tabelle ist dort die Wahrheit, keine
 * Lücke.
 */
export const AUFSTIEG_6: readonly string[] = [
  `CREATE TABLE ExamItemContainerPredefinedCommentPhraseList (
     id TEXT PRIMARY KEY,
     examItemContainerID TEXT NOT NULL REFERENCES ExamItemContainer(id) ON DELETE CASCADE,
     predefinedCommentPhraseListID TEXT NOT NULL
       REFERENCES PredefinedCommentPhraseList(id) ON DELETE CASCADE,
     UNIQUE (examItemContainerID, predefinedCommentPhraseListID)
   )`,
];

/**
 * Fassung 7: `Exam.candidateInstructions` — Hinweise an die Prüflinge zur ganzen Prüfung
 * (Relay `1901219`, 2026-10-09). HTML, aus QTI-3-Paketen oder Relays Prüfungsmaske.
 *
 * ⚠️ **Nur gespeichert, nicht angezeigt** (Entscheidung des Nutzers, 2026-10-09). Wer es
 * anzeigt, bereinigt beim Anzeigen (`domain/htmlSicher.ts`), nicht beim Speichern — so bleibt
 * das Original erhalten.
 *
 * Classic und die Beispielprüfungen kennen das Feld nicht: `NULL` ist dort die Wahrheit.
 */
export const AUFSTIEG_7: readonly string[] = ['ALTER TABLE Exam ADD COLUMN candidateInstructions TEXT'];

/**
 * Fassung 8: `Exam.devicePassword` — das optionale Prüfungspasswort in der Ablageform
 * `pbkdf2-sha512$runden$salz$hash` (`domain/geraetePasswort.ts`, Relay `00426bb`). Gesetzt,
 * fragt das Gerät es vor dem Löschen der Prüfung ab. Nie Klartext.
 */
export const AUFSTIEG_8: readonly string[] = ['ALTER TABLE Exam ADD COLUMN devicePassword TEXT'];

/**
 * Fassung 9: `ExamItem.pointsStep` — die Punkteschrittweite einer Aufgabe beim Setzen von Hand
 * (Relay `items[].pointsStep`, `00426bb`): `1`, `0.5`, `0.25` … `NULL` heisst nicht angegeben,
 * dann gilt die Einstellung halbe/ganze Punkte. Classic führt sie nicht (Entscheidung des
 * Nutzers, 2026-10-09: dort bleibt die Einstellung).
 */
export const AUFSTIEG_9: readonly string[] = ['ALTER TABLE ExamItem ADD COLUMN pointsStep REAL'];

/**
 * Fassung 10: `Exam.settings` — Einstellungen nur für diese Prüfung (Relay `settings` in
 * `exam/document`, `8c2461a`), JSON `{"name": "Wert als Text"}`. Nur Namen mit `jePruefung`,
 * nie Passwörter (`persistence/pruefungsEinstellungen.ts`). `NULL`: keine — Classic und Demo.
 */
export const AUFSTIEG_10: readonly string[] = ['ALTER TABLE Exam ADD COLUMN settings TEXT'];

/**
 * Fassung 11: `Exam.preview` — die Prüfung kam als Preview über Message (`src/preview/`,
 * Nutzer, 2026-10-25). Eine Preview ist zugleich `simulative = 'Y'`; das setzt das Einspielen
 * selbst, auch wenn das Dokument es nicht sagt.
 *
 * ⚠️ **Wozu dann eine eigene Spalte?** Eine echte Prüfung gleicher Kennung darf eine Preview
 * nie überschreiben, und die Prüfungsauswahl markiert sie. Gelöscht wird sie von Hand wie jede
 * andere Prüfung (Entscheidung des Nutzers) — anders als bei Ink räumt Stage nicht selbst auf.
 *
 * Fehlt das Feld (IndexedDB, Classic, Demo), ist die Prüfung keine Preview: dort gibt es keinen
 * Aufstieg der Sätze, `undefined` zählt wie `'N'`.
 */
export const AUFSTIEG_11: readonly string[] = [
  "ALTER TABLE Exam ADD COLUMN preview TEXT NOT NULL DEFAULT 'N' CHECK (preview IN ('Y','N'))",
];

/**
 * Die Aufstiegskette — **die** Liste, nicht eine von mehreren.
 *
 * ⚠️ **Bis zum 2026-09-22 stand sie dreimal da**: im Geräteadapter (`sqlite.ts`, als
 * `addUpgradeStatement`), im Testadapter (`src/test/sqliteStore.ts`) und — mit E4 — am
 * Desktop. Drei Orte, an denen eine Fassung 7 vergessen werden kann, und zwei davon fallen
 * nicht im Testlauf auf. Dieselbe Lehre wie bei `SCHEMA_FASSUNG` in `web.ts` am 2026-09-18:
 * eine Zahl, die niemand abglich, stand ein halbes Jahr falsch da.
 *
 * ⭐ **Fassung 1 ist `DDL`.** Wer eine Fassung anhängt, hängt sie hier an; die Adapter lesen
 * nur noch ab. `schema.test.ts` wacht darüber, dass das letzte `bis` `SCHEMA_VERSION` ist.
 */
export interface Aufstieg {
  readonly bis: number;
  readonly saetze: readonly string[];
}

export const AUFSTIEGE: readonly Aufstieg[] = [
  { bis: 1, saetze: DDL },
  { bis: 2, saetze: AUFSTIEG_2 },
  { bis: 3, saetze: AUFSTIEG_3 },
  { bis: 4, saetze: AUFSTIEG_4 },
  { bis: 5, saetze: AUFSTIEG_5 },
  { bis: 6, saetze: AUFSTIEG_6 },
  { bis: 7, saetze: AUFSTIEG_7 },
  { bis: 8, saetze: AUFSTIEG_8 },
  { bis: 9, saetze: AUFSTIEG_9 },
  { bis: 10, saetze: AUFSTIEG_10 },
  { bis: 11, saetze: AUFSTIEG_11 },
];
